import { useCallback, useEffect, useState } from "react";
import { Banknote, ChevronDown, ChevronRight, CircleSlash, Plus, RotateCcw, Wallet } from "lucide-react";
import { DatePicker, Select } from "antd";
import dayjs from "dayjs";

import { Button } from "../../../components/ui/button";
import { Input, Textarea } from "../../../components/ui/input";
import { DialogModal } from "../../../components/ui/dialogModal";
import ToastMsg from "../../../components/common/ToastMsg";
import useApiMethods from "../../../lib/useApiMethods";
import { EMPLOYEE_DEPOSITS } from "../../../constants/api";
import { checkUserPermissionFunc } from "../../../components/common/CommonFunctions";
import { cn } from "../../../utils/tailwindUtils";
import { modalDescriptionFieldProps, modalFieldLabelClass, modalInputClass, modalTextareaClass } from "../../../components/ui/fieldStyles";
import { isOwnEmployeeProfile } from "../../../utils/employeeProfile";
import { DepositMovementTrace, canReverseDepositTxn } from "../../../components/employee/DepositMovementTrace";
import { formatDepositTraceLine } from "../../../utils/depositStatementDisplay";

const PURPOSE = [
    { value: "security", label: "Security" },
    { value: "equipment", label: "Equipment" },
    { value: "uniform", label: "Uniform" },
    { value: "accommodation", label: "Accommodation" },
    { value: "other", label: "Other" },
];

const COLLECTION_MODE = [
    { value: "cash", label: "Cash" },
    { value: "bank_transfer", label: "Bank transfer" },
    { value: "cheque", label: "Cheque" },
    { value: "salary_deduction", label: "Deduct from salary" },
];

// Direction is in the label, not just the colour — this is the control where
// picking the wrong option moves real money the wrong way.
const MOVEMENT = [
    { value: "collected", label: "Collected  (+ money received)" },
    { value: "applied", label: "Applied  (− taken against a fine or dues)" },
    { value: "refunded", label: "Refunded  (− paid back)" },
    { value: "forfeited", label: "Forfeited  (− kept by the organisation)" },
];

const NEEDS_REASON = ["applied", "forfeited"];

const STATUS_CHIP = {
    active: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
    closed: "bg-gray-100 text-gray-600 dark:bg-gray-700/40 dark:text-gray-300",
    forfeited: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
};

const money = (value) =>
    Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const EMPTY_OPEN = {
    purpose: "security", agreed_amount: "", collection_mode: "cash", collected_amount: "",
    instalment_count: "", reference: "", opened_on: dayjs(), notes: "",
};
const EMPTY_MOVE = { txn_type: "collected", amount: "", reference: "", reason: "", txn_date: dayjs() };

const EmployeeDepositContent = ({ employeeID, readOnly = false }) => {
    const { fetch, create, update } = useApiMethods();
    const viewingOwnProfile = isOwnEmployeeProfile(employeeID);

    const depositPerm = checkUserPermissionFunc("employee", "employee deposits", "SubMenu");
    const employeesPerm = checkUserPermissionFunc("employee", "employees", "SubMenu");
    // Admins bypass the permission tree, as the API does and as the other screens
    // in this app do. An employee looking at their OWN profile can see what is
    // held but never move it — the balance is the organisation's record, not
    // theirs to adjust.
    const isAdmin =
        JSON.parse(sessionStorage.getItem("is_admin") || "false") ||
        JSON.parse(sessionStorage.getItem("is_super_admin") || "false") ||
        JSON.parse(sessionStorage.getItem("is_system_admin") || "false");
    const canManage =
        !readOnly && !viewingOwnProfile &&
        (isAdmin || depositPerm.add || depositPerm.edit || employeesPerm.edit);

    const [deposits, setDeposits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState({});
    const [statements, setStatements] = useState({});
    const [busy, setBusy] = useState(false);

    const [openDialog, setOpenDialog] = useState(false);
    const [openForm, setOpenForm] = useState(EMPTY_OPEN);
    const [moveFor, setMoveFor] = useState(null);
    const [moveForm, setMoveForm] = useState(EMPTY_MOVE);
    const [reverseFor, setReverseFor] = useState(null);
    const [reverseReason, setReverseReason] = useState("");
    const [closeFor, setCloseFor] = useState(null);
    const [closeAs, setCloseAs] = useState("refunded");
    const [closeReason, setCloseReason] = useState("");

    const load = useCallback(async () => {
        if (!employeeID) return [];
        setLoading(true);
        const response = await fetch(EMPLOYEE_DEPOSITS, { employee_id: employeeID, page_size: 100 });
        setLoading(false);
        if (response?.status === "success") {
            const rows = response.data?.result || [];
            setDeposits(rows);
            return rows;
        }
        if (response?.status === "fail") ToastMsg("error", response.message);
        return [];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [employeeID]);

    useEffect(() => {
        load();
    }, [load]);

    const loadStatement = async (deposit) => {
        const depositId = deposit?.id;
        if (depositId == null) return;
        const response = await fetch(EMPLOYEE_DEPOSITS, { id: depositId });
        if (response?.status === "success") {
            const rows = response.data?.statement;
            setStatements((prev) => ({
                ...prev,
                [depositId]: Array.isArray(rows) ? rows : [],
            }));
        } else {
            setStatements((prev) => ({ ...prev, [depositId]: [] }));
            ToastMsg("error", response?.message || "Could not load the statement");
        }
    };

    const reloadExpandedStatements = async (depositRows) => {
        const openDeposits = (depositRows || []).filter((d) => expanded[d.id]);
        await Promise.all(openDeposits.map((d) => loadStatement(d)));
    };

    const toggle = (deposit) => {
        const isOpen = !!expanded[deposit.id];
        setExpanded((prev) => ({ ...prev, [deposit.id]: !isOpen }));
        if (!isOpen && !statements[deposit.id]) loadStatement(deposit);
    };

    const totals = deposits.reduce(
        (acc, d) => ({
            agreed: acc.agreed + Number(d.agreed_amount || 0),
            collected: acc.collected + Number(d.collected || 0),
            taken: acc.taken + Number(d.applied || 0) + Number(d.forfeited || 0),
            refunded: acc.refunded + Number(d.refunded || 0),
            held: acc.held + Number(d.balance || 0),
        }),
        { agreed: 0, collected: 0, taken: 0, refunded: 0, held: 0 },
    );

    const openDeposit = async () => {
        const isSalary = openForm.collection_mode === "salary_deduction";
        const agreed = openForm.agreed_amount === "" ? 0 : Number(openForm.agreed_amount);
        const collected = isSalary || openForm.collected_amount === "" ? 0 : Number(openForm.collected_amount);
        if (!isSalary && agreed > 0 && collected > agreed) {
            ToastMsg("error", "Collected amount cannot exceed the agreed deposit limit");
            return;
        }
        setBusy(true);
        const response = await create(EMPLOYEE_DEPOSITS, {
            employee_id: employeeID,
            purpose: openForm.purpose,
            agreed_amount: agreed,
            collection_mode: openForm.collection_mode,
            collected_amount: collected,
            instalment_count: isSalary && openForm.instalment_count !== "" ? Number(openForm.instalment_count) : null,
            reference: openForm.reference,
            opened_on: openForm.opened_on ? dayjs(openForm.opened_on).format("YYYY-MM-DD") : null,
            notes: openForm.notes,
        });
        setBusy(false);
        if (response?.status === "success") {
            ToastMsg("success", response.message);
            setOpenDialog(false);
            setOpenForm(EMPTY_OPEN);
            load();
        } else {
            const fieldError = response?.errors && Object.values(response.errors)[0];
            ToastMsg("error", fieldError || response?.message || "Could not open the deposit");
        }
    };

    const post = async (body, onDone) => {
        if (
            body.txn_type === "collected"
            && moveFor
            && body.amount > 0
        ) {
            const agreed = Number(moveFor.agreed_amount || 0);
            const already = Number(moveFor.collected || 0);
            if (agreed > 0 && already + Number(body.amount) > agreed) {
                ToastMsg("error", `Cannot collect more than the agreed deposit limit (₹${agreed.toLocaleString("en-IN")})`);
                return;
            }
        }
        setBusy(true);
        const response = await update(EMPLOYEE_DEPOSITS, body);
        setBusy(false);
        if (response?.status === "success") {
            ToastMsg("success", response.message);
            onDone();
            const rows = await load();
            await reloadExpandedStatements(rows);
        } else {
            ToastMsg("error", response?.message || "Action failed");
        }
    };

    const moveNeedsReason = NEEDS_REASON.includes(moveForm.txn_type);
    const remainingCollectible = moveFor
        ? Math.max(0, Number(moveFor.agreed_amount || 0) - Number(moveFor.collected || 0))
        : 0;
    const collectedAfter =
        moveFor && moveForm.amount !== "" && moveForm.txn_type === "collected"
            ? Number(moveFor.collected || 0) + Number(moveForm.amount)
            : null;
    const balanceAfter =
        moveFor && moveForm.amount !== ""
            ? moveForm.txn_type === "collected"
                ? Number(moveFor.balance) + Number(moveForm.amount)
                : Number(moveFor.balance) - Number(moveForm.amount)
            : moveFor?.balance;
    const isSalaryMode = openForm.collection_mode === "salary_deduction";

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
                        <Wallet className="h-5 w-5 text-sidebar-primary" />
                        Deposits
                    </h2>
                    <p className="text-sm text-muted-foreground">
                        Money the organisation holds for this employee, and what has happened to it.
                    </p>
                </div>
                {canManage && (
                    <Button size="sm" onClick={() => setOpenDialog(true)} className=" bg-sidebar text-white hover:bg-sidebar-primary">
                        <Plus className="h-4 w-4 mr-1" />
                        Open Deposit
                    </Button>
                )}
            </div>

            {/* Totals. 'Held' is the liability, so it is the one emphasised. */}
            {deposits.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                    {[
                        { label: "Agreed", value: totals.agreed },
                        { label: "Collected", value: totals.collected },
                        { label: "Taken", value: totals.taken },
                        { label: "Refunded", value: totals.refunded },
                        { label: "Held", value: totals.held, accent: true },
                    ].map((tile) => (
                        <div
                            key={tile.label}
                            className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 p-3"
                        >
                            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{tile.label}</p>
                            <p
                                className={cn(
                                    "text-base font-semibold tabular-nums",
                                    tile.accent ? "text-sidebar-primary" : "text-gray-900 dark:text-white",
                                )}
                            >
                                ₹ {money(tile.value)}
                            </p>
                        </div>
                    ))}
                </div>
            )}

            {loading ? (
                <p className="py-10 text-center text-muted-foreground">Loading deposits</p>
            ) : !deposits.length ? (
                <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 py-12 text-center">
                    <Wallet className="mx-auto h-8 w-8 text-muted-foreground/50" />
                    <p className="mt-2 text-sm text-muted-foreground">No deposits held for this employee.</p>
                    {canManage && (
                        <Button variant="outline" size="sm" className="mt-3" onClick={() => setOpenDialog(true)}>
                            <Plus className="h-4 w-4 mr-1" />
                            Open first deposit
                        </Button>
                    )}
                </div>
            ) : (
                <div className="space-y-3">
                    {deposits.map((d) => {
                        const isOpen = !!expanded[d.id];
                        const statement = statements[d.id];
                        return (
                            <div
                                key={d.id}
                                className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
                            >
                                <div className="flex flex-wrap items-center justify-between gap-3 p-3">
                                    <button
                                        type="button"
                                        className="flex items-center gap-2 text-left"
                                        onClick={() => toggle(d)}
                                    >
                                        {isOpen ? (
                                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                        ) : (
                                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                        )}
                                        <span>
                                            <span className="font-medium capitalize text-gray-900 dark:text-white">
                                                {d.purpose}
                                            </span>
                                            <span className="ml-2 text-xs text-muted-foreground">
                                                agreed ₹ {money(d.agreed_amount)}
                                                {d.opened_on ? ` · opened ${dayjs(d.opened_on).format("DD-MM-YYYY")}` : ""}
                                                {d.reference ? ` · ${d.reference}` : ""}
                                            </span>
                                        </span>
                                    </button>

                                    <div className="flex items-center gap-3">
                                        <span className="text-sm">
                                            held{" "}
                                            <b className="tabular-nums text-sidebar-primary">₹ {money(d.balance)}</b>
                                        </span>
                                        <span
                                            className={cn(
                                                "px-2 py-1 text-xs font-medium rounded-full capitalize",
                                                STATUS_CHIP[d.status] || STATUS_CHIP.active,
                                            )}
                                        >
                                            {d.status}
                                        </span>
                                        {canManage && d.status === "active" && (
                                            <>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => {
                                                        setMoveFor(d);
                                                        setMoveForm(EMPTY_MOVE);
                                                    }}
                                                >
                                                    <Banknote className="h-4 w-4 mr-1" />
                                                    Movement
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    title="Close this deposit"
                                                    className="text-rose-600 hover:bg-rose-50 dark:hover:bg-gray-700"
                                                    onClick={() => {
                                                        setCloseFor(d);
                                                        setCloseAs("refunded");
                                                        setCloseReason("");
                                                    }}
                                                >
                                                    <CircleSlash className="h-4 w-4" />
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {isOpen && (
                                    <div className="border-t border-gray-200 dark:border-gray-700 overflow-x-auto">
                                        <table className="min-w-full text-sm">
                                            <thead className="bg-gray-50 dark:bg-gray-800">
                                                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                                                    <th className="px-3 py-2 font-medium">Date</th>
                                                    <th className="px-3 py-2 font-medium">Movement</th>
                                                    <th className="px-3 py-2 font-medium text-right">Amount</th>
                                                    <th className="px-3 py-2 font-medium text-right">Balance</th>
                                                    <th className="px-3 py-2 font-medium">Reference / reason</th>
                                                    {canManage && <th className="px-3 py-2" />}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {!statement ? (
                                                    <tr>
                                                        <td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">
                                                            Loading statement…
                                                        </td>
                                                    </tr>
                                                ) : statement.length === 0 ? (
                                                    <tr>
                                                        <td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">
                                                            Nothing collected yet.
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    statement.map((txn) => (
                                                        <tr
                                                            key={txn.id}
                                                            className="border-t border-gray-200 dark:border-gray-700"
                                                        >
                                                            <td className="px-3 py-2 whitespace-nowrap tabular-nums">
                                                                {txn.txn_date ? dayjs(txn.txn_date).format("DD-MM-YYYY") : "-"}
                                                            </td>
                                                            <td className="px-3 py-2">
                                                                <DepositMovementTrace txn={txn} />
                                                            </td>
                                                            <td
                                                                className={cn(
                                                                    "px-3 py-2 text-right tabular-nums",
                                                                    txn.direction === "in"
                                                                        ? "text-emerald-700 dark:text-emerald-400"
                                                                        : "text-rose-700 dark:text-rose-400",
                                                                )}
                                                            >
                                                                {txn.direction === "in" ? "+" : "−"} {money(txn.amount)}
                                                            </td>
                                                            <td className="px-3 py-2 text-right tabular-nums font-medium">
                                                                {money(txn.balance)}
                                                            </td>
                                                            <td className="px-3 py-2 text-xs text-muted-foreground">
                                                                {txn.reference}
                                                                {txn.reference && txn.reason ? " · " : ""}
                                                                {txn.reason}
                                                            </td>
                                                            {canManage && (
                                                                <td className="px-3 py-2 text-right">
                                                                    {/* No edit path, ever. A mistake is corrected by a
                                                                        reversal that stands beside the original. */}
                                                                    {canReverseDepositTxn(txn, d.status) && (
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            title="Reverse this movement"
                                                                            className="text-muted-foreground"
                                                                            onClick={() => {
                                                                                setReverseFor({ deposit: d, txn });
                                                                                setReverseReason("");
                                                                            }}
                                                                        >
                                                                            <RotateCcw className="h-4 w-4" />
                                                                        </Button>
                                                                    )}
                                                                </td>
                                                            )}
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {viewingOwnProfile && deposits.length > 0 && (
                <p className="text-[11px] text-muted-foreground">
                    This is your own record, so it is read-only here. Raise anything that looks wrong with HR.
                </p>
            )}

            {/* Open a deposit */}
            <DialogModal
                open={openDialog}
                onClose={() => {
                    setOpenDialog(false);
                    setOpenForm(EMPTY_OPEN);
                }}
                onSave={openDeposit}
                title="Open a deposit"
                saveLabel="Open"
                closeLabel="Cancel"
                loading={busy}
                width="600px"
            >
                <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="space-y-2">
                            <label className={modalFieldLabelClass}>For</label>
                            <Select
                                size="large"
                                className="w-full"
                                value={openForm.purpose}
                                options={PURPOSE}
                                onChange={(v) => setOpenForm((p) => ({ ...p, purpose: v }))}
                                disabled={busy}
                            />
                        </div>
                        <div className="space-y-2">
                            <label htmlFor="dep-agreed" className={modalFieldLabelClass}>
                                Agreed amount <span className="text-red-500">*</span>
                            </label>
                            <Input
                                id="dep-agreed"
                                type="number"
                                min="0"
                                value={openForm.agreed_amount}
                                onChange={(e) => setOpenForm((p) => ({ ...p, agreed_amount: e.target.value }))}
                                placeholder="5000"
                                disabled={busy}
                            />
                        </div>
                        <div className="space-y-2">
                            <label className={modalFieldLabelClass}>Opened on</label>
                            <DatePicker
                                className="w-full"
                                format="DD-MM-YYYY"
                                value={openForm.opened_on}
                                onChange={(date) => setOpenForm((p) => ({ ...p, opened_on: date }))}
                                disabled={busy}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="space-y-2">
                            <label className={modalFieldLabelClass}>
                                How it is collected
                            </label>
                            <Select
                                size="large"
                                className="w-full"
                                value={openForm.collection_mode}
                                options={COLLECTION_MODE}
                                onChange={(v) => setOpenForm((p) => ({ ...p, collection_mode: v }))}
                                disabled={busy}
                            />
                        </div>
                        {isSalaryMode ? (
                            <div className="space-y-2">
                                <label htmlFor="dep-inst" className={modalFieldLabelClass}>
                                    Over how many months <span className="text-red-500">*</span>
                                </label>
                                <Input
                                    id="dep-inst"
                                    type="number"
                                    min="1"
                                    value={openForm.instalment_count}
                                    onChange={(e) => setOpenForm((p) => ({ ...p, instalment_count: e.target.value }))}
                                    placeholder="5"
                                    disabled={busy}
                                />
                            </div>
                        ) : (
                            <div className="space-y-2">
                                <label htmlFor="dep-recv" className={modalFieldLabelClass}>
                                    Received now
                                </label>
                                <Input
                                    id="dep-recv"
                                    type="number"
                                    min="0"
                                    value={openForm.collected_amount}
                                    onChange={(e) => setOpenForm((p) => ({ ...p, collected_amount: e.target.value }))}
                                    placeholder="5000"
                                    disabled={busy}
                                />
                            </div>
                        )}
                        <div className="space-y-2">
                            <label htmlFor="dep-ref" className={modalFieldLabelClass}>
                                Reference
                            </label>
                            <Input
                                id="dep-ref"
                                value={openForm.reference}
                                onChange={(e) => setOpenForm((p) => ({ ...p, reference: e.target.value }))}
                                placeholder="Receipt or cheque number"
                                disabled={busy}
                            />
                        </div>
                    </div>

                    <p className="text-[11px] text-muted-foreground">
                        {isSalaryMode
                            ? "Nothing is collected yet — the instalments come off salary month by month and the balance builds up as they are taken."
                            : "Anything entered as received now is recorded on the ledger straight away."}
                    </p>

                    <div className="space-y-2">
                        <label htmlFor="dep-notes" className={modalFieldLabelClass}>
                            Notes
                        </label>
                        <Textarea
                            id="dep-notes"
                            rows={2}
                            value={openForm.notes}
                            onChange={(e) => setOpenForm((p) => ({ ...p, notes: e.target.value }))}
                            disabled={busy}
                        />
                    </div>
                </form>
            </DialogModal>

            {/* Record a movement */}
            <DialogModal
                open={!!moveFor}
                onClose={() => {
                    setMoveFor(null);
                    setMoveForm(EMPTY_MOVE);
                }}
                onSave={() =>
                    post(
                        {
                            id: moveFor.id,
                            txn_type: moveForm.txn_type,
                            amount: moveForm.amount === "" ? 0 : Number(moveForm.amount),
                            reference: moveForm.reference,
                            reason: moveForm.reason,
                            txn_date: moveForm.txn_date ? dayjs(moveForm.txn_date).format("YYYY-MM-DD") : null,
                        },
                        () => {
                            setMoveFor(null);
                            setMoveForm(EMPTY_MOVE);
                        },
                    )
                }
                title="Deposit movement"
                saveLabel="Record"
                closeLabel="Cancel"
                loading={busy}
                width="520px"
            >
                <div className="space-y-4">
                    {/* The remaining balance, first thing. It is the number that
                        decides whether the amount below is even allowed, so it
                        belongs above the field and not under it. */}
                    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 px-3 py-2.5">
                        <div className="flex items-baseline justify-between gap-3">
                            <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                                Remaining deposit
                            </span>
                            <b className="text-lg tabular-nums text-gray-900 dark:text-gray-100">
                                ₹ {money(moveFor?.balance)}
                            </b>
                        </div>
                        {moveForm.txn_type === "collected" && Number(moveFor?.agreed_amount || 0) > 0 && (
                            <div className="mt-1.5 flex items-baseline justify-between gap-3 border-t border-gray-200 dark:border-gray-700 pt-1.5">
                                <span className="text-xs text-gray-600 dark:text-gray-400">Still collectible (agreed limit)</span>
                                <b className="tabular-nums text-gray-900 dark:text-gray-100">
                                    ₹ {money(remainingCollectible)}
                                </b>
                            </div>
                        )}
                        {moveForm.amount !== "" && (
                            <div className="mt-1.5 flex items-baseline justify-between gap-3 border-t border-gray-200 dark:border-gray-700 pt-1.5">
                                <span className="text-xs text-gray-600 dark:text-gray-400">After this movement</span>
                                <b
                                    className={cn(
                                        "tabular-nums",
                                        balanceAfter < 0
                                            ? "text-rose-600 dark:text-rose-400"
                                            : "text-gray-900 dark:text-gray-100",
                                    )}
                                >
                                    ₹ {money(balanceAfter)}
                                </b>
                            </div>
                        )}
                        {moveForm.amount !== "" && balanceAfter < 0 && (
                            <p className="mt-1 text-[11px] text-rose-600 dark:text-rose-400">
                                More than is held — this will be refused.
                            </p>
                        )}
                        {collectedAfter !== null
                            && Number(moveFor?.agreed_amount || 0) > 0
                            && collectedAfter > Number(moveFor.agreed_amount) && (
                            <p className="mt-1 text-[11px] text-rose-600 dark:text-rose-400">
                                Exceeds agreed deposit limit — this will be refused.
                            </p>
                        )}
                    </div>
                    <div className="space-y-2">
                        <label className={modalFieldLabelClass}>
                            What happened <span className="text-red-500">*</span>
                        </label>
                        <Select
                            size="large"
                            className="w-full"
                            value={moveForm.txn_type}
                            options={MOVEMENT}
                            onChange={(v) => setMoveForm((p) => ({ ...p, txn_type: v }))}
                            disabled={busy}
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <label htmlFor="mv-amt" className={modalFieldLabelClass}>
                                Amount <span className="text-red-500">*</span>
                            </label>
                            <Input
                                id="mv-amt"
                                type="number"
                                min="0"
                                value={moveForm.amount}
                                onChange={(e) => setMoveForm((p) => ({ ...p, amount: e.target.value }))}
                                disabled={busy}
                            />
                        </div>
                        <div className="space-y-2">
                            <label className={modalFieldLabelClass}>Date</label>
                            <DatePicker
                                className="w-full"
                                format="DD-MM-YYYY"
                                value={moveForm.txn_date}
                                onChange={(date) => setMoveForm((p) => ({ ...p, txn_date: date }))}
                                disabled={busy}
                            />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <label htmlFor="mv-ref" className={modalFieldLabelClass}>
                            Reference
                        </label>
                        <Input
                            id="mv-ref"
                            value={moveForm.reference}
                            onChange={(e) => setMoveForm((p) => ({ ...p, reference: e.target.value }))}
                            placeholder="Receipt, cheque or transaction number"
                            disabled={busy}
                        />
                    </div>
                    <div className="space-y-2">
                        <label htmlFor="mv-reason" className={modalFieldLabelClass}>
                            Reason {moveNeedsReason && <span className="text-red-500">*</span>}
                        </label>
                        <Input
                            id="mv-reason"
                            value={moveForm.reason}
                            onChange={(e) => setMoveForm((p) => ({ ...p, reason: e.target.value }))}
                            placeholder="Lost ID card"
                            maxLength={255}
                            disabled={busy}
                        />
                        {moveNeedsReason && (
                            <p className="text-[11px] text-muted-foreground">
                                Required, and kept on the record — it is what answers the question if the employee
                                asks later why the money came back short.
                            </p>
                        )}
                    </div>
                </div>
            </DialogModal>

            {/* Reverse */}
            <DialogModal
                open={!!reverseFor}
                onClose={() => setReverseFor(null)}
                onSave={() =>
                    post(
                        {
                            id: reverseFor.deposit.id,
                            reverse_txn_id: reverseFor.txn.id,
                            reason: reverseReason,
                        },
                        () => setReverseFor(null),
                    )
                }
                title="Reverse this movement"
                saveLabel="Reverse"
                closeLabel="Cancel"
                loading={busy}
                width="460px"
            >
                <div className="space-y-3">
                    {reverseFor?.txn && (
                        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 px-3 py-2.5 text-sm">
                            <p className="text-xs font-medium text-muted-foreground mb-1">Movement being reversed</p>
                            <p className="tabular-nums text-gray-900 dark:text-gray-100">
                                {formatDepositTraceLine(reverseFor.txn)}
                            </p>
                        </div>
                    )}
                    <p className="text-sm text-muted-foreground">
                        The original stays on the statement with this correction beside it. Nothing is edited or
                        removed.
                    </p>
                    <div className="space-y-2">
                        <label htmlFor="rv-reason" className={modalFieldLabelClass}>
                            Reason <span className="text-red-500">*</span>
                        </label>
                        <Input
                            id="rv-reason"
                            value={reverseReason}
                            onChange={(e) => setReverseReason(e.target.value)}
                            placeholder="Recorded twice"
                            maxLength={255}
                            disabled={busy}
                        />
                    </div>
                </div>
            </DialogModal>

            {/* Close */}
            <DialogModal
                open={!!closeFor}
                onClose={() => setCloseFor(null)}
                onSave={() =>
                    post(
                        {
                            id: closeFor.id,
                            close: true,
                            settle_as: closeFor.balance > 0 ? closeAs : null,
                            reason: closeReason,
                        },
                        () => setCloseFor(null),
                    )
                }
                title="Close the deposit"
                saveLabel="Close deposit"
                closeLabel="Cancel"
                loading={busy}
                width="480px"
            >
                <div className="space-y-3">
                    {closeFor?.balance > 0 ? (
                        <>
                            <p className="text-sm">
                                <b className="tabular-nums">₹ {money(closeFor.balance)}</b> is still held. Decide what
                                happens to it — a deposit cannot be closed with money left on it.
                            </p>
                            <div className="space-y-2">
                                <label className={modalFieldLabelClass}>
                                    Settle it as
                                </label>
                                <Select
                                    size="large"
                                    className="w-full"
                                    value={closeAs}
                                    options={[
                                        { value: "refunded", label: "Refunded to the employee" },
                                        { value: "forfeited", label: "Forfeited — kept by the organisation" },
                                    ]}
                                    onChange={setCloseAs}
                                    disabled={busy}
                                />
                            </div>
                            <div className="space-y-2">
                                <label htmlFor="cl-reason" className={modalFieldLabelClass}>
                                    Reason {closeAs === "forfeited" && <span className="text-red-500">*</span>}
                                </label>
                                <Input
                                    id="cl-reason"
                                    value={closeReason}
                                    onChange={(e) => setCloseReason(e.target.value)}
                                    placeholder={
                                        closeAs === "forfeited" ? "Equipment not returned" : "Left the organisation"
                                    }
                                    maxLength={255}
                                    disabled={busy}
                                />
                            </div>
                        </>
                    ) : (
                        <p className="text-sm text-muted-foreground">
                            Nothing is held on this deposit, so it can be closed as settled.
                        </p>
                    )}
                </div>
            </DialogModal>
        </div>
    );
};

export default EmployeeDepositContent;
